import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    LocalFSAdapter,
    FSNode,
    FSFileNode
} from '../../../persistence/LocalFSAdapter';
import './SamplesBrowser.css';
import '../../../index.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import {
    useFSAdapter,
    useSetFSAdapter
} from '../../../stores/WorkspaceStore';


export function SamplesBrowser({
    initialRoot = null,
    includeMetadata = true,
    onSelectFile,
    className,
    style,
}: {
    initialRoot?: FileSystemDirectoryHandle | null;
    includeMetadata?: boolean;
    onSelectFile?: (file: FSFileNode, adapter: LocalFSAdapter) => void;
    className?: string;
    style?: React.CSSProperties;
}) {
    const rootHandle = useFSAdapter();
    const setRootHandle = useSetFSAdapter();
    const [error, setError] = useState<string | null>(null);

    const supported = LocalFSAdapter.isSupported();

    const pickDir = useCallback(async () => {
        try {
            setError(null);
            const handle = await LocalFSAdapter.pickDirectory();
            if (!handle) return;
            const adapter = new LocalFSAdapter();
            adapter.setRoot(handle);
            setRootHandle(adapter);
        } catch (e: any) {
            setError(e?.message ?? String(e));
        }
    }, []);

    if (!supported) {
        return (
            <div className={"interaction-panel"} style={style}>
                <div style={{ color: '#b00' }}>This browser does not support the File System Access API.</div>
            </div>
        );
    }

    return (
        <div className={"interaction-panel"} style={{ ...style, display: 'flex', flexDirection: 'column', minHeight: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <button onClick={pickDir}>Pick directory</button>
                <div style={{ color: '#666' }}>
                    {rootHandle ? rootHandle.getRoot().name : 'No directory selected'}
                </div>
            </div>
            {error ? (
                <div style={{ color: '#b00' }}>{error}</div>
            ) : rootHandle ? (

                <DirectoryRow
                    adapter={rootHandle}
                    name={rootHandle.getRoot().name}
                    path=""
                    depth={0}
                    includeMetadata={includeMetadata}
                    initiallyExpanded
                    onSelectFile={onSelectFile}
                />

            ) : (
                <div style={{ color: '#666' }}>Choose a directory to browse.</div>
            )}
        </div>
    );
};

function DirectoryRow(props: {
    adapter: LocalFSAdapter;
    name: string;
    path: string;
    depth: number;
    includeMetadata: boolean;
    initiallyExpanded?: boolean;
    onSelectFile?: (file: FSFileNode, adapter: LocalFSAdapter) => void;
}) {
    const { adapter, name, path, depth, includeMetadata, initiallyExpanded = true, onSelectFile } = props;
    const [expanded, setExpanded] = useState<boolean>(initiallyExpanded);
    const [children, setChildren] = useState<FSNode[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const load = useCallback(async () => {
        try {
            setError(null);
            setLoading(true);
            const kids = await adapter.listDirectory(path, includeMetadata);
            setChildren(kids);
        } catch (e: any) {
            setError(e?.message ?? String(e));
        } finally {
            setLoading(false);
        }
    }, [adapter, path, includeMetadata]);

    useEffect(() => {
        if (expanded && children === null) {
            void load();
        }
    }, [expanded, children, load, refreshKey]);

    const toggle = useCallback(() => {
        setExpanded((e) => !e);
    }, []);

    const refresh = useCallback(() => {
        setChildren(null);
        setRefreshKey((k) => k + 1);
        setExpanded(true);
    }, []);

    return (
        <div>
            <Row
                depth={depth}
                isDir
                expanded={expanded}
                loading={loading}
                name={name}
                onToggle={toggle}
                onRefresh={refresh}
                error={error}
            />
            {expanded && (
                <div>
                    {loading && <Row depth={depth + 1} name="Loading..." dim />}
                    {error && !loading && <Row depth={depth + 1} name={`Error: ${error}`} dim />}
                    {!loading &&
                        !error &&
                        (children?.length ? (
                            children!.map((ch) =>
                                ch.kind === 'directory' ? (
                                    <DirectoryRow
                                        key={`dir:${ch.path}`}
                                        adapter={adapter}
                                        name={ch.name}
                                        path={ch.path}
                                        depth={depth + 1}
                                        includeMetadata={includeMetadata}
                                        initiallyExpanded={false}
                                        onSelectFile={onSelectFile}
                                    />
                                ) : (
                                    <FileRow
                                        key={`file:${ch.path}`}
                                        depth={depth + 1}
                                        node={ch}
                                        onClick={() => onSelectFile && onSelectFile(ch, adapter)}
                                    />
                                ),
                            )
                        ) : (
                            <Row depth={depth + 1} name="(empty)" dim />
                        ))}
                </div>
            )}
        </div>
    );
}

function FileRow({ depth, node, onClick }: { depth: number; node: FSFileNode; onClick?: () => void }) {
    return (
        <div className='ryd-filerow'
            title={node.path}
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: onClick ? 'pointer' : 'default',
                // height: rowHeight,
                paddingLeft: depth * 14 + 6,
            }}
        >
            <span aria-hidden>📄</span>
            <span style={{ flex: 1 }} className='ryd-filerow'>{node.name}</span>
            <button aria-description='Preview' style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faPlay} /></button>
        </div>
    );
}

function Row(props: {
    depth: number;
    name: string;
    isDir?: boolean;
    expanded?: boolean;
    loading?: boolean;
    error?: string | null;
    dim?: boolean;
    onToggle?: () => void;
    onRefresh?: () => void;
}) {
    const { depth, name, isDir, expanded, loading, dim, onToggle, onRefresh } = props;
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                paddingLeft: depth * 14,
                color: dim ? '#888' : undefined,
            }}
        >
            {isDir ? (
                <button
                    onClick={onToggle}
                    title={expanded ? 'Collapse' : 'Expand'}
                    style={{ textAlign: 'center' }}
                >
                    {expanded ? '▾' : '▸'}
                </button>
            ) : (
                <span style={{ width: 22 }} />
            )}
            <span aria-hidden>{isDir ? '📁' : '📄'}</span>
            <span style={{ flex: 1, userSelect: 'none' }}>{name}</span>
            {isDir && (
                <button onClick={onRefresh} disabled={!!loading} title="Refresh directory" style={{ textAlign: 'center' }}>
                    ↻
                </button>
            )}
        </div>
    );
}